import React, {useEffect, useRef, useState} from 'react';
import {
    Create,
    Edit,
    FileInput,
    ImageField,
    SimpleForm,
    useGetList,
    usePermissions,
    useRecordContext,
    useSaveContext,
    useTranslate,
} from 'react-admin';
import {useFormContext, useWatch} from 'react-hook-form';
import {Avatar, Box, Typography} from '@mui/material';
import {DeleteOutline} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import StatusDot from '../components/statamic/StatusDot';
import StatamicTextInput from '../components/statamic/StatamicTextInput';
import StatamicSelectInput from '../components/statamic/StatamicSelectInput';
import ToggleRow from '../components/statamic/ToggleRow';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import useTranslatedCountries from '../lib/useTranslatedCountries';
import {useUmbrellaUuidChoices} from '../lib/customHooks';
import {isUuid} from '../dataprovider/springDataRest';
import hasPermission from '../auth/hasPermission';
import DynamicMetadataFields from '../components/DynamicMetadataFields';

const TIMEZONE_CHOICES = (translate) => [
    {id: '', name: translate('Inherit from umbrella')},
    {id: 'Europe/Amsterdam', name: 'Amsterdam (CET)'},
    {id: 'Europe/London', name: 'London (GMT)'},
    {id: 'Europe/Paris', name: 'Paris (CET)'},
    {id: 'Europe/Berlin', name: 'Berlin (CET)'},
    {id: 'America/New_York', name: 'New York (EST)'},
    {id: 'America/Los_Angeles', name: 'Los Angeles (PST)'},
];

const CURRENCY_CHOICES = (translate) => [
    {id: '', name: translate('Inherit from umbrella')},
    {id: 'EUR', name: '€ Euro (EUR)'},
    {id: 'GBP', name: '£ British Pound (GBP)'},
    {id: 'USD', name: '$ US Dollar (USD)'},
    {id: 'CHF', name: 'Swiss Franc (CHF)'},
];

const LOCALE_CHOICES = (translate) => [
    {id: '', name: translate('Inherit from umbrella')},
    {id: 'nl_NL', name: 'Nederlands'},
    {id: 'en_GB', name: 'English (UK)'},
    {id: 'en_US', name: 'English (US)'},
    {id: 'de_DE', name: 'Deutsch'},
    {id: 'fr_FR', name: 'Français'},
];

const urlFormat = (v) => (!v || v === 'null' || v === 'undefined' || v === 'NULL') ? '' : v;
// v2 explicit-value contract: an empty string CLEARS these three fields server-side
// (unlike every other field, where '' is stored verbatim) — return '' here, not null,
// so a cleared field still reaches the provider as an explicit value instead of being
// dropped as "untouched."
const urlParse = (v) => (!v || v === 'null' || v === 'undefined' || !v.trim()) ? '' : v;
const urlParseStripTrailingSlash = (v) => {
    const parsed = urlParse(v);
    return parsed ? parsed.trim().replace(/\/$/, '') : parsed;
};

const SaveAction = ({label}) => {
    const {handleSubmit} = useFormContext();
    const {save, saving} = useSaveContext();
    return (
        <PrimaryButton variant="primary" disabled={saving} onClick={handleSubmit(save)}>
            {saving ? 'Saving…' : label}
        </PrimaryButton>
    );
};

const buildBreadcrumb = (record, translate) => {
    if (record?.parent_charity_id && record?.parent_charity_name) {
        return [
            {label: translate('Umbrellas'), to: '/umbrellas'},
            {label: record.parent_charity_name, to: `/umbrellas/${record.parent_charity_id}/show`},
            {label: record.charity_name || translate('Edit charity')},
        ];
    }
    return {label: translate('Charities'), to: '/charities'};
};

const HeaderBar = ({mode, record, translate, canDelete, onDelete}) => {
    const statusVariant = record?.demo_charity ? 'info' : record?.active ? 'success' : 'neutral';
    const breadcrumb = mode === 'create'
        ? {label: translate('Charities'), to: '/charities'}
        : buildBreadcrumb(record, translate);

    return (
        <PageHeader
            breadcrumb={breadcrumb}
            title={
                mode === 'create' ? (
                    <Box component="span">{translate('Create charity')}</Box>
                ) : (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={statusVariant}/>
                        <Box component="span">{record?.charity_name || 'Unnamed'}</Box>
                    </Box>
                )
            }
            actions={
                <>
                    <SaveAction label={mode === 'create' ? translate('Create charity') : translate('Save changes')}/>
                    {mode === 'edit' && canDelete && (
                        <PrimaryButton
                            variant="danger"
                            startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                            onClick={onDelete}
                        >
                            {translate('Delete')}
                        </PrimaryButton>
                    )}
                </>
            }
        />
    );
};

const LogoPanel = () => {
    const record = useRecordContext();
    const existingLogo = record?.logo_name && record?.ext
        ? `${process.env.REACT_APP_CDN_URL}/${record.logo_name}.${record.ext}`
        : undefined;

    return (
        <SectionCard title="Logo">
            {existingLogo && (
                <Box sx={{display: 'flex', justifyContent: 'center', mb: 1.5}}>
                    <Avatar
                        src={existingLogo}
                        variant="rounded"
                        sx={{
                            width: 96, height: 96,
                            fontSize: 32,
                            bgcolor: t.color.sidebarHover,
                            color: t.color.text.secondary,
                            border: `1px solid ${t.color.border.hairline}`,
                        }}
                    >
                        {record?.charity_name?.charAt(0)}
                    </Avatar>
                </Box>
            )}
            <FileInput
                source="logo"
                label={false}
                accept={{'image/*': []}}
                maxSize={500000}
                sx={{
                    '& .RaFileInput-dropZone': {
                        border: `1px dashed ${t.color.border.default}`,
                        borderRadius: `${t.radius.sm}px`,
                        backgroundColor: t.color.surface,
                        padding: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        color: t.color.text.secondary,
                        fontSize: `${t.type.caption.size}px`,
                        '&:hover': {
                            borderColor: t.color.accent.primary,
                            color: t.color.accent.primary,
                        },
                    },
                }}
            >
                <ImageField source="src" title="Logo"/>
            </FileInput>
            <Typography sx={{fontSize: 11, color: t.color.text.muted, mt: 1}}>
                PNG or JPG. Maximum 500 KB.
            </Typography>
        </SectionCard>
    );
};

const CoordinatorSelector = () => {
    const translate = useTranslate();
    const record = useRecordContext();
    const {setValue} = useFormContext();

    // v2 volunteers are scoped by the "charity_id" filter key carrying the charity's
    // UUID (the people vertical's own external-id param, distinct from children's
    // "charity_external_id") — sanitizePeopleFilter drops charity_external_id
    // unconditionally and strips a non-UUID charity_id, so anything else here is
    // silently unscoped. record.id is always the UUID external id for v2 charities
    // (no numeric id exists in the model); record.external_id is a defensive fallback
    // for a shape that does not currently occur.
    const coordinatorScopeUuid = isUuid(record?.id) ? record?.id : (isUuid(record?.external_id) ? record?.external_id : null);

    const {data: volunteers, isLoading: volunteersLoading, error: volunteersError} = useGetList(
        'volunteers',
        {
            filter: {charity_id: coordinatorScopeUuid},
            // 200 is cheap headroom over the prior 100 — still a single page, just less
            // likely to push a real coordinator past the cutoff and read as "stale."
            pagination: {page: 1, perPage: 200},
            sort: {field: 'given_name', order: 'ASC'},
        },
        {enabled: !!coordinatorScopeUuid}
    );

    const coordinatorChoices = (volunteers || []).map(v => ({
        id: v.id,
        name: `${v.given_name || ''} ${v.family_name || ''}`.trim() || v.email,
    }));

    // v2 rejects a stale/unknown coordinator id with a 400 on save (v1 silently
    // ignored it) — once the volunteer list has loaded (successfully, in full — not
    // merely "not loading," which is also true on a failed fetch), if the record's
    // stored coordinator isn't among this charity's current volunteers, force the
    // field back to "None" so an unmodified save doesn't resubmit the now-invalid id.
    // A failed fetch must NOT be treated as "confirmed absent" — that would silently
    // clear a perfectly valid coordinator just because the list couldn't load.
    const volunteersLoaded = !volunteersLoading && !volunteersError && Array.isArray(volunteers);
    const currentCoordinatorId = record?.default_project_coordinator_id;
    const isStale = volunteersLoaded && !!currentCoordinatorId
        && !coordinatorChoices.some(c => String(c.id) === String(currentCoordinatorId));

    useEffect(() => {
        if (volunteersLoaded && isStale) {
            setValue('default_project_coordinator_id', '', {shouldDirty: false});
        }
    }, [volunteersLoaded, isStale, setValue]);

    if (!record?.id) {
        return (
            <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.secondary}}>
                {translate('charity.save_first_for_coordinator')}
            </Typography>
        );
    }

    return (
        <>
            <StatamicSelectInput
                source="default_project_coordinator_id"
                label={translate('charity.default_project_coordinator')}
                choices={[{id: '', name: translate('charity.none', {_: 'None'})}, ...coordinatorChoices]}
                helperText={translate('charity.coordinator_help_text')}
            />
            {volunteersError ? (
                <Typography sx={{fontSize: 11, color: t.color.text.muted, mt: 0.5}}>
                    {translate('charity.coordinator_list_unavailable', {_: 'Coordinator list unavailable'})}
                </Typography>
            ) : isStale && (
                <Typography sx={{fontSize: 11, color: t.color.text.muted, mt: 0.5}}>
                    {translate('charity.coordinator_stale', {_: 'Previous coordinator no longer available'})}
                </Typography>
            )}
        </>
    );
};

const FormBody = ({mode}) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const countries = useTranslatedCountries();
    const umbrellaChoices = useUmbrellaUuidChoices();
    const {permissions} = usePermissions();
    const deleteConfirm = useDeleteConfirm();
    const {setValue} = useFormContext();

    const canSeeUmbrellaFields = hasPermission(permissions, ['view'], 'umbrella');
    // v2 flips charity delete to umbrella_admin-only — admin now 403/405s server-side.
    const canDelete = permissions === 'umbrella';

    const canSeeLocations = !!hasPermission(permissions, ['create', 'edit'], 'charity.location');
    // v2's admin-path locations endpoint REQUIRES umbrella_id (400 without it) — an
    // admin only has an umbrella to source locations from once this charity is
    // parented to one, so track the live form value (re-parenting mid-edit should
    // re-scope the fetch) falling back to the record's existing parent on first load.
    const parentUuid = useWatch({name: 'parent_charity_external_id'}) ?? record?.parent_charity_id;
    const locationsFetchEnabled = canSeeLocations && (permissions !== 'admin' || !!parentUuid);
    const [locationChoices, setLocationChoices] = useState([]);
    const {data: locations, error: locationsError} = useGetList(
        'locations',
        {
            pagination: {page: 1, perPage: 100},
            sort: {field: 'title', order: 'ASC'},
            filter: permissions === 'admin' ? {umbrella_id: parentUuid} : {},
        },
        {enabled: locationsFetchEnabled}
    );

    useEffect(() => {
        setLocationChoices((locations || []).map((l) => ({id: l.id, name: l.title})));
    }, [locations]);
    // Clear the selected location whenever the parent umbrella actually CHANGES — a
    // location id from the OLD umbrella must not silently ride along to the new one
    // (the server does no ownership validation on location_id). Tracked against the
    // PREVIOUS parentUuid (not the record's stored parent) so this also catches
    // create-mode re-parenting, where there is no record to compare against: pick
    // umbrella A, pick a location, switch to umbrella B — B must not inherit A's
    // location. The `prevParentUuid.current !== undefined` guard means the very
    // first value parentUuid ever takes (undefined on a fresh create form, or
    // whatever it resolves to on the first edit-mode render) is only ever recorded,
    // never treated as a "change" — so initial data hydration (record loading in,
    // populating parentUuid from record.parent_charity_id) never spuriously clears
    // location_id. Only a genuine subsequent change trips the clear.
    const prevParentUuid = useRef(parentUuid);
    useEffect(() => {
        if (prevParentUuid.current !== undefined && parentUuid !== prevParentUuid.current) {
            setValue('location_id', '', {shouldDirty: true});
        }
        prevParentUuid.current = parentUuid;
    }, [parentUuid, setValue]);

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <HeaderBar
                mode={mode}
                record={record}
                translate={translate}
                canDelete={canDelete}
                onDelete={deleteConfirm.openDialog}
            />

            {record && (
                <DeleteConfirmDialog
                    open={deleteConfirm.open}
                    onClose={deleteConfirm.closeDialog}
                    resource="charities"
                    record={record}
                    recordLabel={record.charity_name}
                />
            )}

            <Box sx={{p: 3, display: 'grid', gridTemplateColumns: '1fr 320px', gap: 2, alignItems: 'start'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
                    <SectionCard title={translate('General info')}>
                        {canSeeUmbrellaFields && (
                            <Box sx={{mb: 2}}>
                                <StatamicSelectInput
                                    source="parent_charity_external_id"
                                    label={translate('Umbrella')}
                                    required
                                    choices={umbrellaChoices || []}
                                    defaultValue={record?.parent_charity_id}
                                />
                            </Box>
                        )}
                        {locationsFetchEnabled && (locationsError || locationChoices.length > 0) && (
                            <Box sx={{mb: 2}}>
                                {locationsError ? (
                                    <Typography sx={{fontSize: 11, color: t.color.text.muted}}>
                                        {translate('charity.locations_unavailable', {_: 'Locations unavailable'})}
                                    </Typography>
                                ) : (
                                    <StatamicSelectInput
                                        source="location_id"
                                        label={translate('Location')}
                                        choices={locationChoices}
                                    />
                                )}
                            </Box>
                        )}
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2}}>
                            <StatamicTextInput source="short_name" label={translate('Short name')} required/>
                            <StatamicTextInput source="charity_name" label={translate('Name')} required/>
                            <StatamicTextInput
                                source="sponsorship_amount"
                                label={translate('Sponsorship amount')}
                                type="number"
                                required
                            />
                        </Box>
                        <Box sx={{mt: 2}}>
                            <StatamicTextInput source="website_link" label={translate('Website link')} type="url"/>
                        </Box>
                        <Box sx={{mt: 2}}>
                            <StatamicTextInput
                                source="description"
                                label={translate('Description')}
                                required
                                multiline
                                rows={4}
                            />
                        </Box>
                    </SectionCard>

                    <SectionCard title={translate('Payment')}>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                            <StatamicTextInput
                                source="payment_link_url"
                                label={translate('Stripe Payment Link URL')}
                                type="url"
                                format={urlFormat}
                                parse={urlParse}
                                helperText={translate('Stripe payment link for guardians to complete sponsorship')}
                            />
                            <StatamicTextInput
                                source="paypal_payment_link_url"
                                label={translate('PayPal Payment Link URL')}
                                type="url"
                                format={urlFormat}
                                parse={urlParse}
                                helperText={translate('PayPal payment link for guardians to complete sponsorship')}
                            />
                        </Box>
                    </SectionCard>

                    <SectionCard title={translate('Child Detail Pages')}>
                        <StatamicTextInput
                            source="child_detail_base_url"
                            label={translate('Child Detail Base URL')}
                            type="url"
                            format={urlFormat}
                            parse={urlParseStripTrailingSlash}
                            helperText={translate('Override umbrella URL for child detail pages (leave empty to inherit)')}
                        />
                    </SectionCard>

                    <SectionCard title={translate('Mail settings')}>
                        <Box sx={{mb: 2}}>
                            <StatamicTextInput source="email" label={translate('email')} type="email" required/>
                        </Box>
                        <ToggleRow
                            source="send_new_post_mail"
                            label={translate('Send new post mail?')}
                            defaultValue={false}
                        />
                    </SectionCard>

                    <SectionCard title={translate('Address')}>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <StatamicTextInput source="city" label={translate('City')} required/>
                            <StatamicSelectInput
                                source="country"
                                label={translate('Country')}
                                required
                                choices={countries}
                            />
                        </Box>
                        <Box sx={{mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <StatamicTextInput source="nr_of_countries" label={translate('Nr of countries')} type="number" required/>
                            <StatamicTextInput source="nr_of_projects" label={translate('Nr of projects')} type="number" required/>
                        </Box>
                        <Box sx={{mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <StatamicTextInput source="lat" label="Lat" type="number"/>
                            <StatamicTextInput source="lng" label="Long" type="number"/>
                        </Box>
                    </SectionCard>

                    <SectionCard title={translate('Message Type Settings')}>
                        <ToggleRow
                            source="allow_text_messages"
                            label={translate('Allow text messages')}
                            defaultValue={true}
                        />
                        <ToggleRow
                            source="allow_photo_messages"
                            label={translate('Allow photo messages')}
                            defaultValue={true}
                        />
                        <ToggleRow
                            source="allow_video_messages"
                            label={translate('Allow video messages')}
                            defaultValue={true}
                        />
                    </SectionCard>

                    <SectionCard title={translate('Additional Information')}>
                        <DynamicMetadataFields entityType="CHARITY" mode={mode}/>
                    </SectionCard>
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 16}}>
                    <SectionCard title={translate('Status')}>
                        <ToggleRow
                            source="active"
                            label={translate('Is active')}
                            defaultValue={true}
                        />
                        {canSeeUmbrellaFields && (
                            <ToggleRow
                                source="demo_charity"
                                label="Demo"
                                subtitle="Mark as demo/test charity"
                                defaultValue={false}
                            />
                        )}
                    </SectionCard>

                    <LogoPanel/>

                    <SectionCard title={translate('Localization Settings')} subtitle={translate('Leave empty to inherit from umbrella organization')}>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                            <StatamicSelectInput
                                source="default_timezone"
                                label={translate('Timezone Override')}
                                choices={TIMEZONE_CHOICES(translate)}
                            />
                            <StatamicSelectInput
                                source="default_currency"
                                label={translate('Currency Override')}
                                choices={CURRENCY_CHOICES(translate)}
                            />
                            <StatamicSelectInput
                                source="default_locale"
                                label={translate('Locale Override')}
                                choices={LOCALE_CHOICES(translate)}
                            />
                        </Box>
                    </SectionCard>

                    {mode === 'edit' && (
                        <SectionCard title={translate('charity.default_project_coordinator_section')}>
                            <CoordinatorSelector/>
                        </SectionCard>
                    )}

                    <SectionCard title={translate('Alert info')}>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                            <StatamicTextInput
                                source="days_orange_alert"
                                label={translate('Days orange alert')}
                                type="number"
                                required
                            />
                            <StatamicTextInput
                                source="days_red_alert"
                                label={translate('Days red alert')}
                                type="number"
                                required
                            />
                        </Box>
                    </SectionCard>
                </Box>
            </Box>
        </Box>
    );
};

export const CharityEditStatamic = (props) => (
    <Edit {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="show">
        <SimpleForm toolbar={false}>
            <FormBody mode="edit"/>
        </SimpleForm>
    </Edit>
);

export const CharityCreateStatamic = (props) => (
    <Create {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="show">
        <SimpleForm toolbar={false}>
            <FormBody mode="create"/>
        </SimpleForm>
    </Create>
);

export default CharityEditStatamic;
